import { OfflineFieldQueuePanel } from "@/components/OfflineFieldQueuePanel";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { incidentsKey, incidentsQuery } from "@/lib/screen-queries";
import { useAuth } from "@/lib/auth";
import { isGuard } from "@/lib/roles";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Search, AlertTriangle, ArrowRight, Clock, MapPin } from "lucide-react";
import {
  INCIDENT_CATEGORIES, INCIDENT_SEVERITIES, INCIDENT_STATUSES,
  SEVERITY_STYLES, STATUS_STYLES,
} from "@/lib/incident-types";

export const Route = createFileRoute("/_authenticated/incidents/")({
  component: IncidentsPage,
  head: () => ({ meta: [{ title: "Incidents — MissionReady" }] }),
});

type Incident = {
  id: string; reference_number: string; site_id: string | null;
  category: string; subtype: string | null; severity: string; status: string;
  title: string | null; narrative: string | null;
  occurrence_at: string; created_at: string; reporter_id: string; assignee_id: string | null;
};

type Site = { id: string; name: string };

function IncidentsPage() {
  const { currentOrg, currentRole } = useAuth();
  const guardView = isGuard(currentRole);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSeverity, setFilterSeverity] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [search, setSearch] = useState("");

  // Same cache entry the prefetcher warms, so a prepared screen renders at once.
  const qc = useQueryClient();
  const orgId = currentOrg?.id ?? "";
  const { data, isPending } = useQuery(incidentsQuery(orgId));
  const items = (data?.items ?? []) as Incident[];
  const sites = (data?.sites ?? []) as Site[];
  const loading = !!orgId && isPending;
  const load = async () => {
    if (!orgId) return;
    await qc.invalidateQueries({ queryKey: incidentsKey(orgId) });
  };

  // realtime
  useEffect(() => {
    if (!orgId) return;
    const ch = supabase.channel(`incidents-${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "incidents", filter: `organization_id=eq.${orgId}` }, () => { void load(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgId]);

  const siteName = (id: string | null) => id ? (sites.find((s) => s.id === id)?.name ?? "—") : "Unassigned";

  const filtered = useMemo(() => items.filter((i) =>
    (filterStatus === "all" || i.status === filterStatus) &&
    (filterSeverity === "all" || i.severity === filterSeverity) &&
    (filterCategory === "all" || i.category === filterCategory) &&
    (search === "" ||
      i.reference_number.toLowerCase().includes(search.toLowerCase()) ||
      (i.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (i.subtype ?? "").toLowerCase().includes(search.toLowerCase()))
  ), [items, filterStatus, filterSeverity, filterCategory, search]);

  const counts = useMemo(() => ({
    total: items.length,
    open: items.filter((i) => i.status === "open").length,
    in_progress: items.filter((i) => i.status === "in_progress").length,
    critical: items.filter((i) => i.severity === "critical" && i.status !== "closed").length,
  }), [items]);

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Operations Center</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">{guardView ? "My Reports" : "Incidents"}</h1>
          <p className="text-sm text-muted-foreground">
            {guardView
              ? "Your incident history, drafts, and follow-ups."
              : "Field reports, follow-ups, and shift records."}
          </p>
        </div>
        <Button asChild size="sm"><Link to="/incidents/new"><Plus className="mr-1 h-4 w-4" /> New report</Link></Button>
      </div>

      <OfflineFieldQueuePanel kind="incident" />


      {/* Stat tiles */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total", value: counts.total },
          { label: "Open", value: counts.open },
          { label: "In progress", value: counts.in_progress },
          { label: "Critical", value: counts.critical, accent: true },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
            <div className={`mt-1 font-display text-3xl font-semibold ${s.accent ? "text-destructive" : ""}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input aria-label="Search by reference, title, type" className="pl-9" placeholder="Search by reference, title, type…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {INCIDENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severity</SelectItem>
            {INCIDENT_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {INCIDENT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="mt-4 rounded-xl border border-border bg-card">
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
            {items.length === 0 ? "No reports yet — file your first one." : "No reports match your filters."}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((i) => (
              <li key={i.id}>
                <Link to="/incidents/$incidentId" params={{ incidentId: i.id }} className="flex items-start gap-4 px-5 py-4 hover:bg-muted/30">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{i.reference_number}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${SEVERITY_STYLES[i.severity]}`}>{i.severity}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLES[i.status]}`}>{i.status.replace("_"," ")}</span>
                    </div>
                    <div className="mt-1 truncate font-medium">
                      {i.title || i.subtype || INCIDENT_CATEGORIES.find((c) => c.value === i.category)?.label}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{siteName(i.site_id)}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(i.occurrence_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <ArrowRight className="mt-3 h-4 w-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
